import { useCallback, useEffect, useState } from 'react';
import { ExternalLink, MapPin, PackageCheck, Truck } from 'lucide-react';
import type { CaseStatus, ReturnMethod } from '../../shared/case-schema.js';
import { api } from '../api.js';
import { AppShell } from '../components/AppShell.js';
import { CaseTimeline, type PublicHistoryEntry } from '../components/CaseTimeline.js';
import { StatusBadge, statusLabel } from '../components/StatusBadge.js';
import { Button, Field, Input, Loading, Notice, Textarea } from '../components/ui.js';

interface GuestCase {
  reference: string;
  venue: { name: string; city: string };
  item: { description: string; category: string; color: string };
  status: CaseStatus;
  version: number;
  history: PublicHistoryEntry[];
  returnMethod?: ReturnMethod;
  shipping?: {
    recipientName: string;
    address: string;
    phone?: string;
    instructions?: string;
    submittedAt: string;
  };
  pickupInstructions?: string;
  shipment?: {
    carrier: string;
    trackingNumber: string;
    trackingUrl?: string;
    shippedAt: string;
  };
}

export function TrackingPage() {
  const [caseRecord, setCaseRecord] = useState<GuestCase | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      setCaseRecord(await api<GuestCase>('/api/tracking/case'));
      setError(null);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Tracking is unavailable');
    }
  }, []);
  useEffect(() => void load(), [load]);

  if (!caseRecord) {
    return (
      <AppShell language="en">
        {error ? (
          <section className="center-card">
            <h1>Tracking unavailable</h1>
            <Notice tone="danger">{error}</Notice>
          </section>
        ) : (
          <Loading label="Loading your return case..." />
        )}
      </AppShell>
    );
  }

  return (
    <AppShell language="en">
      <div className="case-layout">
        <header className="case-header">
          <div>
            <span className="eyebrow">Report {caseRecord.reference}</span>
            <h1>{statusLabel(caseRecord.status)}</h1>
            <p>
              {caseRecord.item.description} · {caseRecord.venue.name}, {caseRecord.venue.city}
            </p>
          </div>
          <StatusBadge status={caseRecord.status} />
        </header>
        <NextGuestAction caseRecord={caseRecord} setCaseRecord={setCaseRecord} reload={load} />
        <section className="flow-card">
          <h2>Case history</h2>
          <CaseTimeline history={caseRecord.history} />
        </section>
      </div>
    </AppShell>
  );
}

function NextGuestAction({
  caseRecord,
  setCaseRecord,
  reload,
}: {
  caseRecord: GuestCase;
  setCaseRecord: (value: GuestCase) => void;
  reload: () => Promise<void>;
}) {
  if (caseRecord.status === 'POSSIBLE_MATCH') {
    return <ReturnDetailsForm caseRecord={caseRecord} onSaved={setCaseRecord} reload={reload} />;
  }
  if (caseRecord.status === 'SHIPPED' || caseRecord.status === 'READY_FOR_PICKUP') {
    return <ConfirmReturnCard caseRecord={caseRecord} onSaved={setCaseRecord} reload={reload} />;
  }

  const content = {
    SUBMITTED: {
      title: 'The venue has your report',
      message: 'The venue team will review the information and update this case.',
    },
    REVIEWING: {
      title: 'The venue is checking',
      message: 'No action is needed from you while the venue looks for a match.',
    },
    RETURN_DETAILS_PROVIDED: {
      title: 'Your return instructions were sent',
      message:
        caseRecord.returnMethod === 'SHIP'
          ? 'The venue will arrange shipping and add tracking information here.'
          : 'The venue will add pickup instructions here when the item is ready.',
    },
    RETURNED: {
      title: 'Return complete',
      message: 'This item has been marked as returned. No further action is needed.',
    },
    CLOSED_NOT_FOUND: {
      title: 'No match was found',
      message: 'The venue closed the current review without finding a matching item.',
    },
  } as const;
  const state = content[caseRecord.status];
  return (
    <section className="flow-card case-next-action">
      <PackageCheck aria-hidden />
      <div>
        <h2>{state.title}</h2>
        <p>{state.message}</p>
      </div>
    </section>
  );
}

function ReturnDetailsForm({
  caseRecord,
  onSaved,
  reload,
}: {
  caseRecord: GuestCase;
  onSaved: (value: GuestCase) => void;
  reload: () => Promise<void>;
}) {
  const [method, setMethod] = useState<ReturnMethod>('SHIP');
  const [recipientName, setRecipientName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [instructions, setInstructions] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const body =
        method === 'SHIP'
          ? {
              expectedVersion: caseRecord.version,
              method,
              recipientName,
              address,
              ...(phone.trim() ? { phone } : {}),
              ...(instructions.trim() ? { instructions } : {}),
            }
          : { expectedVersion: caseRecord.version, method };
      onSaved(
        await api<GuestCase>('/api/tracking/return-details', {
          method: 'POST',
          body: JSON.stringify(body),
        }),
      );
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Could not save details');
      await reload();
    } finally {
      setSaving(false);
    }
  };
  return (
    <section className="flow-card return-details-card">
      <span className="eyebrow">Action needed</span>
      <h2>How should the venue return your item?</h2>
      <p>Shipping is recommended. The venue will arrange the courier or postal service.</p>
      <div className="return-methods">
        <button
          type="button"
          className={`choice-card ${method === 'SHIP' ? 'selected' : ''}`}
          onClick={() => setMethod('SHIP')}
        >
          <Truck aria-hidden />
          <span>
            <strong>Ship the item</strong>
            <small>Provide the address where the venue should send it</small>
          </span>
          <span className="radio-dot" />
        </button>
        <button
          type="button"
          className={`choice-card ${method === 'PICKUP' ? 'selected' : ''}`}
          onClick={() => setMethod('PICKUP')}
        >
          <MapPin aria-hidden />
          <span>
            <strong>Pick up at the venue</strong>
            <small>The venue will provide collection instructions</small>
          </span>
          <span className="radio-dot" />
        </button>
      </div>
      {method === 'SHIP' ? (
        <div className="shipping-fields">
          <Field label="Recipient name">
            <Input
              value={recipientName}
              onChange={(event) => setRecipientName(event.target.value)}
            />
          </Field>
          <Field label="Where should we send the item?" hint="Enter a complete postal address.">
            <Textarea value={address} onChange={(event) => setAddress(event.target.value)} />
          </Field>
          <Field label="Phone, optional">
            <Input type="tel" value={phone} onChange={(event) => setPhone(event.target.value)} />
          </Field>
          <Field label="Delivery instructions, optional">
            <Textarea
              value={instructions}
              onChange={(event) => setInstructions(event.target.value)}
            />
          </Field>
        </div>
      ) : null}
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <div className="flow-actions single">
        <Button
          disabled={saving || (method === 'SHIP' && (!recipientName.trim() || !address.trim()))}
          onClick={() => void submit()}
        >
          {saving ? 'Saving...' : 'Send return instructions'}
        </Button>
      </div>
    </section>
  );
}

function ConfirmReturnCard({
  caseRecord,
  onSaved,
  reload,
}: {
  caseRecord: GuestCase;
  onSaved: (value: GuestCase) => void;
  reload: () => Promise<void>;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirm = async () => {
    setSaving(true);
    setError(null);
    try {
      onSaved(
        await api<GuestCase>('/api/tracking/confirm-return', {
          method: 'POST',
          body: JSON.stringify({ expectedVersion: caseRecord.version }),
        }),
      );
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Could not confirm return');
      await reload();
    } finally {
      setSaving(false);
    }
  };
  return (
    <section className="flow-card fulfillment-card">
      {caseRecord.status === 'SHIPPED' && caseRecord.shipment ? (
        <>
          <Truck aria-hidden />
          <div>
            <span className="eyebrow">Shipment</span>
            <h2>Your item is on its way</h2>
            <p>
              <strong>{caseRecord.shipment.carrier}</strong> · Tracking{' '}
              {caseRecord.shipment.trackingNumber}
            </p>
            {caseRecord.shipment.trackingUrl ? (
              <a
                href={caseRecord.shipment.trackingUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-link"
              >
                Track with the carrier <ExternalLink size={15} aria-hidden />
              </a>
            ) : null}
          </div>
        </>
      ) : (
        <>
          <MapPin aria-hidden />
          <div>
            <span className="eyebrow">Pickup</span>
            <h2>Your item is ready for pickup</h2>
            <p>{caseRecord.pickupInstructions}</p>
          </div>
        </>
      )}
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <div className="flow-actions single">
        <Button disabled={saving} onClick={() => void confirm()}>
          {saving ? 'Confirming...' : 'I received my item'}
        </Button>
      </div>
    </section>
  );
}
